import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Heart, MapPin, Pencil, Send, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import api, { imgSrc, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { CampusMap } from "@/components/CampusMap";
import { ScrollReveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function AnimalDetail() {
  const { id } = useParams();
  const { user, status } = useAuth();
  const [animal, setAnimal] = useState(null);
  const [comments, setComments] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [posting, setPosting] = useState(false);
  const navigate = useNavigate();

  const deleteEntry = async () => {
    if (!window.confirm("Delete this entry permanently? Its margin notes go with it.")) return;
    try {
      await api.delete(`/animals/${id}`);
      toast.success("Entry removed from the journal");
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/animals/${id}/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.comment_id !== commentId));
      toast.success("Note removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: c }] = await Promise.all([
        api.get(`/animals/${id}`),
        api.get(`/animals/${id}/comments`),
      ]);
      setAnimal(a);
      setComments(c);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleLike = async () => {
    if (status !== "authed") {
      toast.info("Sign in to like this entry");
      return;
    }
    try {
      const { data } = await api.post(`/animals/${id}/like`);
      setAnimal((prev) => ({ ...prev, liked: data.liked, likes_count: data.likes_count }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      await api.post(`/animals/${id}/comments`, { text: commentText });
      setCommentText("");
      const { data } = await api.get(`/animals/${id}/comments`);
      setComments(data);
      toast.success("Note added to the journal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen" data-testid="animal-not-found">
        <Navbar />
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6">
          <p className="font-serif text-4xl italic text-muted-foreground">This page of the journal is missing.</p>
          <Link to="/" data-testid="not-found-back-link" className="border border-foreground px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors hover:bg-foreground hover:text-background">
            Back to the journal
          </Link>
        </div>
      </div>
    );
  }

  if (!animal) {
    return (
      <div className="min-h-screen" data-testid="animal-loading">
        <Navbar />
        <div className="h-[70vh] animate-pulse bg-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="animal-detail-page">
      <Navbar />

      {/* Hero image */}
      <section className="relative flex h-[72vh] items-end overflow-hidden">
        <motion.img
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          src={imgSrc(animal.image_url)}
          alt={animal.title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative w-full px-6 pb-14 md:px-16">
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}>
            <Link to="/" data-testid="detail-back-link" className="mb-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#f6f3ea]/80 transition-colors hover:text-[#f6f3ea]">
              <ArrowLeft className="h-4 w-4" /> All entries
            </Link>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#f6f3ea]/80">{animal.category}</p>
            <h1 className="mt-3 max-w-4xl font-serif text-5xl font-semibold leading-[0.95] tracking-tighter text-[#f6f3ea] md:text-7xl" data-testid="animal-title">
              {animal.title}
            </h1>
            <p className="mt-3 font-serif text-2xl italic text-[#f6f3ea]/85" data-testid="animal-species">{animal.species}</p>
          </motion.div>
        </div>
      </section>

      {/* Meta strip */}
      <div className="border-b border-border bg-card" data-testid="animal-meta">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-10 gap-y-4 px-6 py-6 md:px-16">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4 text-primary" />
            <span data-testid="animal-author">{animal.author_name}</span>
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 text-primary" />
            {fmtDate(animal.created_at)}
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 text-primary" />
            <span data-testid="animal-location">{animal.location_name}</span>
          </span>
          {user && (user.role === "admin" || user.user_id === animal.author_id) && (
            <>
              <Link
                to={`/edit/${animal.post_id}`}
                data-testid="edit-entry-button"
                className="ml-auto flex items-center gap-2 border border-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background"
              >
                <Pencil className="h-4 w-4" /> Edit
              </Link>
              <button
                onClick={deleteEntry}
                data-testid="delete-entry-button"
                className="flex items-center gap-2 border border-destructive px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-destructive transition-colors duration-300 hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </>
          )}
          <button
            onClick={toggleLike}
            data-testid="like-button"
            className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 ${!(user && (user.role === "admin" || user.user_id === animal.author_id)) ? "ml-auto " : ""}${
              animal.liked
                ? "border-primary bg-primary text-primary-foreground"
                : "border-foreground hover:bg-foreground hover:text-background"
            }`}
          >
            <Heart className={`h-4 w-4 transition-transform duration-300 ${animal.liked ? "fill-current scale-110" : ""}`} />
            <span data-testid="like-count">{animal.likes_count}</span>
          </button>
        </div>
      </div>

      {/* Writings */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-16 md:py-32">
        <div className="mx-auto max-w-2xl space-y-24">
          {animal.chapters && animal.chapters.length > 0 ? (
            animal.chapters.map((ch, i) => (
              <ScrollReveal key={i}>
                <div className="relative" data-testid={`chapter-${i + 1}`}>
                  <span className="absolute -left-20 -top-4 hidden select-none font-serif text-8xl font-semibold text-primary/15 lg:block">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl">{ch.heading}</h2>
                  <p className="mt-6 whitespace-pre-line text-base leading-loose text-foreground/80 md:text-lg">{ch.body}</p>
                </div>
              </ScrollReveal>
            ))
          ) : (
            <p className="font-serif text-2xl italic text-muted-foreground" data-testid="no-writings">No writings yet — just the photograph.</p>
          )}
        </div>
      </section>

      {/* Photo gallery */}
      {animal.photos && animal.photos.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-16" data-testid="photo-gallery">
          <ScrollReveal>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">More encounters</p>
            <h2 className="mb-10 mt-3 font-serif text-3xl font-semibold tracking-tight md:text-4xl">Photo gallery</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {animal.photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setLightbox(p)}
                  data-testid={`gallery-photo-${i}`}
                  className="group relative aspect-square overflow-hidden border border-border"
                >
                  <img
                    src={imgSrc(p)}
                    alt={`${animal.title} — additional photo ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </ScrollReveal>
        </section>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-foreground/85 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <motion.img
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            src={imgSrc(lightbox)}
            alt="Enlarged view"
            className="max-h-[85vh] max-w-full border border-border object-contain"
          />
          <button
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
            className="absolute right-6 top-6 text-xs font-bold uppercase tracking-[0.2em] text-background/80 transition-colors hover:text-background"
          >
            Close ×
          </button>
        </div>
      )}

      {/* Where it was spotted */}
      {animal.lat != null && animal.lng != null && (
        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-16">
          <ScrollReveal>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Spotted at</p>
            <h2 className="mb-8 mt-3 font-serif text-3xl font-semibold tracking-tight md:text-4xl">{animal.location_name}</h2>
            <CampusMap animals={[animal]} height="420px" />
          </ScrollReveal>
        </section>
      )}

      {/* Comments */}
      <section className="border-t border-border bg-card" data-testid="comments-section">
        <div className="mx-auto max-w-2xl px-6 py-24">
          <h2 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl" data-testid="comments-heading">
            Margin notes <span className="text-primary/50">({comments.length})</span>
          </h2>

          <div className="mt-12 space-y-8">
            {comments.map((c) => (
              <ScrollReveal key={c.comment_id}>
                <div className="flex gap-4" data-testid={`comment-${c.comment_id}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-secondary font-serif text-lg font-semibold text-primary">
                    {c.user_name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                  <div className="flex-1 border-b border-border pb-6">
                    <p className="text-sm font-bold">{c.user_name} <span className="ml-2 text-xs font-normal text-muted-foreground">{fmtDate(c.created_at)}</span></p>
                    <p className="mt-2 text-sm leading-relaxed text-foreground/80">{c.text}</p>
                  </div>
                  {user && (user.role === "admin" || user.user_id === c.user_id) && (
                    <button
                      onClick={() => deleteComment(c.comment_id)}
                      data-testid={`delete-comment-${c.comment_id}`}
                      title="Remove note"
                      className="self-start text-muted-foreground transition-colors duration-300 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </ScrollReveal>
            ))}
            {comments.length === 0 && (
              <p className="font-serif text-xl italic text-muted-foreground" data-testid="comments-empty">No notes yet. Start the conversation.</p>
            )}
          </div>

          {status === "authed" ? (
            <form onSubmit={submitComment} className="mt-12" data-testid="comment-form">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                data-testid="comment-input"
                placeholder={`Add a note as ${user?.name || "you"}…`}
                rows={3}
                className="w-full resize-none border border-border bg-background p-4 text-sm outline-none transition-colors duration-300 placeholder:text-muted-foreground focus:border-primary"
              />
              <button
                type="submit"
                disabled={posting || !commentText.trim()}
                data-testid="comment-submit-button"
                className="mt-4 flex items-center gap-2 bg-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
                {posting ? "Posting…" : "Post note"}
              </button>
            </form>
          ) : (
            <div className="mt-12 border border-dashed border-border p-8 text-center" data-testid="comments-signin-prompt">
              <p className="text-sm text-muted-foreground">Sign in to leave a margin note.</p>
              <Link to="/login" data-testid="comments-signin-link" className="mt-4 inline-block border border-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background">
                Sign in
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
